import { useMemo } from "react";
import PageSectionTitle from "../components/PageSectionTitle";
import { ColumnDef } from "@tanstack/react-table";
import { User } from "../types/user.types";
import {
  Avatar,
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuProps,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import {
  useConnectedUser,
  useUsersPaginated,
} from "../hooks/user.hooks";
import MoreIcon from "../components/icons/MoreIcon";
import { deleteUser, updateUser } from "../mutations/user.mutations";
import { useMutation, useQueryClient } from "react-query";
import PrimaryButton from "../components/PrimaryButton";
import PlusIcon from "../components/icons/PlusIcon";
import CreateUserForm from "../components/forms/CreateUserForm";
import i18n from "../i18n";
import { useTryLaterToast } from "../hooks/toast.hooks";
import DataTable, {
  SearchBar,
} from "../components/table/ServerSideTable";
import Select from "../components/inputs/Select";
import UserForm from "../components/forms/UserForm";
import { useCenters } from "../hooks/centers.hooks";
import { userIsAdmin } from "../utils";
import { useDataTableContext } from "../hooks/data-table.hooks";

function MoreMenu({
  user,
  ...props
}: { user: User } & Omit<MenuProps, "children">) {
  const queryClient = useQueryClient();
  const { tryLater } = useTryLaterToast();
  const { mutate: mutateUpdateUser } = useMutation(updateUser, {
    onSuccess: () => {
      queryClient.invalidateQueries("users");
    },
  });
  const { mutate: mutateDeleteUser } = useMutation(deleteUser, {
    onSuccess: () => {
      queryClient.invalidateQueries("users");
    },
  });
  const handleGiveAccessClick = () => {
    mutateUpdateUser(
      { id: user.id, role: "ADMIN" },
      {
        onError: () => {
          tryLater();
        },
      }
    );
  };
  const handleActivateClick = () => {
    mutateUpdateUser(
      { id: user.id, activatedAt: new Date() },
      {
        onError: () => {
          tryLater();
        },
      }
    );
  };
  const handleDesactivateClick = () => {
    mutateUpdateUser(
      { id: user.id, activatedAt: null },
      {
        onError: () => {
          tryLater();
        },
      }
    );
  };
  const handleDeleteUserClick = () => {
    mutateDeleteUser(
      { id: user.id },
      {
        onError: () => {
          tryLater();
        },
      }
    );
  };
  const { onOpen, onClose, isOpen } = useDisclosure();

  return (
    <>
      <Menu {...props}>
        <MenuButton as={Button} variant="outlined" transition="all 0.2s">
          <MoreIcon w="24px" h="24px" />
        </MenuButton>
        <MenuList>
          {!userIsAdmin(user) && (
            <MenuItem lineHeight={"24px"} onClick={handleGiveAccessClick}>
              Donner les accès administrateur
            </MenuItem>
          )}
          {user.activatedAt === null && (
            <MenuItem lineHeight={"24px"} onClick={handleActivateClick}>
              Activer le compte
            </MenuItem>
          )}
          {user.activatedAt !== null && (
            <MenuItem lineHeight={"24px"} onClick={handleDesactivateClick}>
              Désactiver le compte
            </MenuItem>
          )}
          <MenuItem lineHeight={"24px"} onClick={() => onOpen()}>
            Modifier les informations de l'utilisateur
          </MenuItem>
          <MenuItem lineHeight={"24px"} onClick={handleDeleteUserClick}>
            Supprimer l'utilisateur
          </MenuItem>
        </MenuList>
      </Menu>
      {/* Update user information modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size={"xl"}>
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6.9px)" />
        <ModalContent>
          <ModalHeader>Modifier les informations de l'utilisateur</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <UserForm
              user={user}
              onSuccess={() => {
                queryClient.invalidateQueries("users");
                onClose();
              }}
            ></UserForm>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

const UsersTableActionsComponent = () => {
  const { filters, setFilters } = useDataTableContext();

  return (
    <Flex gap={3}>
      {filters && (
        <Select
          placeholder="Rôle"
          value={filters.role}
          onChange={(evt) => {
            if (setFilters) {
              setFilters("role", evt.target.value);
            }
          }}
        >
          <option value="AUDIOPROSTHESIST">Audioprothésiste</option>
          <option value="ASSISTANT">Assistant</option>
          <option value="OTHER">Autre</option>
        </Select>
      )}
      {filters && (
        <Select
          placeholder="Access"
          value={filters.access}
          onChange={(evt) => {
            if (setFilters) {
              setFilters("access", evt.target.value);
            }
          }}
        >
          <option value="ADMIN">Admin</option>
          <option value="MEMBER">Membre</option>
        </Select>
      )}
      {filters && (
        <Select
          placeholder="Statut"
          value={filters.statut}
          onChange={(evt) => {
            if (setFilters) {
              setFilters("statut", evt.target.value);
            }
          }}
        >
          <option value="activated">Activé</option>
          <option value="deactivated">Désactivé</option>
        </Select>
      )}
    </Flex>
  );
};

function SettingsPageUsersPanel() {
  const user = useConnectedUser();
  const { center } = useCenters();
  const columns = useMemo<ColumnDef<User>[]>(() => {
    if (user?.access !== "admin") {
      return [
        {
          accessorFn: (row) => (
            <Flex textTransform="capitalize" alignItems="center" h="44px">
              <Avatar
                name={`${row.firstName} ${row.lastName}`}
                h="40px"
                w="40px"
                mr="20px"
              ></Avatar>
              {row.firstName} {row.lastName}
            </Flex>
          ),
          id: "full_name",
          header: "Utilisateur",
          cell: (info) => info.getValue(),
          sortDescFirst: true,
        },
        {
          accessorFn: (row) => row.email,
          id: "email",
          header: "Email",
          cell: (info) => info.getValue(),
          sortDescFirst: true,
        },
        {
          accessorFn: (row) => (
            <Box textTransform="capitalize">{i18n.t(row.role)}</Box>
          ),
          id: "role",
          header: "Rôle",
          cell: (info) => info.getValue(),
          sortDescFirst: true,
        },
        {
          accessorFn: (row) => (
            <Box textTransform="capitalize">{i18n.t(row.access)}</Box>
          ),
          id: "access",
          header: "Accès",
          cell: (info) => info.getValue(),
          sortDescFirst: true,
        },
        {
          accessorFn: (row) => (
            <Box textTransform="capitalize">
              {row.activatedAt === null ? "Pas activé" : "Activé"}
            </Box>
          ),
          id: "state",
          header: "Status",
          cell: (info) => info.getValue(),
          sortDescFirst: true,
        },
      ];
    }
    return [
      {
        accessorFn: (row) => (
          <Flex textTransform="capitalize" alignItems="center" h="44px">
            <Avatar
              name={`${row.firstName} ${row.lastName}`}
              h="40px"
              w="40px"
              mr="20px"
            ></Avatar>
            {row.firstName} {row.lastName}
          </Flex>
        ),
        id: "full_name",
        header: "Utilisateur",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => row.email,
        id: "email",
        header: "Email",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => (
          <Box textTransform="capitalize">
            {row.activatedAt === null ? "Pas activé" : "Activé"}
          </Box>
        ),
        id: "state",
        header: "Status",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => <MoreMenu user={row}></MoreMenu>,
        id: "more",
        header: "",
        cell: (info) => info.getValue(),
      },
    ];
  }, []);
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      {/* Add modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size={"xl"} closeOnOverlayClick={false}>
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6.9px)" />
        <ModalContent>
          <ModalHeader>Ajouter un utilisateur</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreateUserForm onSubmit={onClose} centerId={center?.id}></CreateUserForm>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Flex justifyContent={"space-between"}>
        <PageSectionTitle>Utilisateurs</PageSectionTitle>
        {(user?.isSuperAdmin || center?.access === "ADMIN") && (
          <PrimaryButton w="14rem" onClick={onOpen}>
            <PlusIcon mr="4px"></PlusIcon>Ajouter un utilisateur
          </PrimaryButton>
        )}
      </Flex>
      <Box w="100%">
        <DataTable
          columns={columns}
          paginatedQuery={useUsersPaginated({
            center: center?.id ? center?.id.toString() : "",
          })}
          w={["14rem", "auto", "12rem", "6rem", "6rem", "2rem"]}
          flexGrow={[0, 1, 0, 0, 0, 0]}
          toolbar={
            <Flex my="1rem" justifyContent="space-between">
              <SearchBar />
              <UsersTableActionsComponent />
            </Flex>
          }
        ></DataTable>
      </Box>
    </>
  );
}

export default SettingsPageUsersPanel;
